import { FormInput, FormSelect, toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { updateUserAffiliateSettings } from 'modules/profile/affiliate-settings/api/affiliate-settings-api';
import AffiliateSettingsSkeleton from 'modules/profile/affiliate-settings/components/affiliate-settings-skeleton';
import BankTransferTable from 'modules/profile/affiliate-settings/components/bank-transfer-table';
import { AffiliateSettingsLoco } from 'modules/profile/affiliate-settings/constants/affiliate-settings-loco';
import { AffiliateWithdrawalTypesEnum } from 'modules/profile/affiliate-settings/enums/affiliate-withdrawal-enum';
import { useAffiliateSettings } from 'modules/profile/affiliate-settings/hooks/use-affiliate-settings';
import { AffiliateSettingsApiInterface, AffiliateSettingsErrorsInterface, AffiliateSettingsErrorsResponseContentInterface, AffiliateSettingsInterface } from 'modules/profile/affiliate-settings/types/affiliate-settings-interface';
import { getAffiliateWithdrawalDataOptions } from 'modules/profile/affiliate-settings/utils/get-affiliate-withdrawal-options';
import SettingsLayout from 'modules/profile/components/settings-layout';
import { settingsMenuLocoKeys } from 'modules/profile/constants/settings-menu-loco-keys';
import { NextPage } from 'next';
import React, { useEffect, useState } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
const defaultErrors: AffiliateSettingsErrorsInterface = {
  fields: {
    payPalCredentials: {
      email: ''
    }
  },
  common: []
};
const defaultValues: AffiliateSettingsInterface = {
  withdrawalMethod: undefined,
  payPalCredentials: {},
  wiseRecipients: [],
  allowedCurrencies: []
};
const AffiliateSettings: NextPage = () => {
  const {
    t
  } = useLocoTranslation();
  const [tempState, setTempState] = useState(defaultValues);
  const [errors, setErrors] = useState(defaultErrors);
  const [isFetching, setIsFetching] = useState(false);
  const affiliateWithdrawalDataOptions = getAffiliateWithdrawalDataOptions(t);
  const {
    userAffiliateSettings,
    mutateAffiliateSettings
  } = useAffiliateSettings();
  const onChangeAffiliateSettings = async () => {
    try {
      setIsFetching(true);
      setErrors(defaultErrors);
      const data: AffiliateSettingsApiInterface = {
        withdrawalMethod: tempState.withdrawalMethod
      };
      if (tempState.withdrawalMethod === AffiliateWithdrawalTypesEnum.paypal) {
        data['payPalCredentials'] = {
          email: tempState.payPalCredentials?.email
        };
      }
      const response = await updateUserAffiliateSettings(data);
      await mutateAffiliateSettings(response.data, false);
      setIsFetching(false);
      toast.success(t('global.changes_saved'));
    } catch (e) {
      setIsFetching(false);
      if (e instanceof BadRequest) {
        const affiliateSettingFieldsErrors = e.errors as unknown as AffiliateSettingsErrorsResponseContentInterface;
        setErrors(prev => ({
          ...prev,
          fields: {
            ...prev.fields,
            payPalCredentials: {
              email: affiliateSettingFieldsErrors.fields.payPalCredentials?.email?.join('')
            }
          }
        }));
        if (e.errors.common) {
          const commonErrors = e.errors.common;
          setErrors(prev_0 => {
            return {
              ...prev_0,
              common: commonErrors
            };
          });
        }
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  useEffect(() => {
    if (userAffiliateSettings) {
      setTempState(userAffiliateSettings);
    }
  }, [userAffiliateSettings]);
  return <SettingsLayout titles={[{
    caption: t(settingsMenuLocoKeys.affiliate_commissions),
    isStatic: true
  }]} isFetching={isFetching} onSave={onChangeAffiliateSettings} disabled={!userAffiliateSettings}>
      {userAffiliateSettings ? <div className="bg-background-secondary flex h-fit w-full flex-col gap-5 overflow-x-hidden rounded-md">
          <div className="flex flex-col gap-7 p-5 lg:gap-10">
            <FormSelect placeholder={t('dashboard.user_settings.affiliate_commissions.payment_method_placeholder_title')} data={affiliateWithdrawalDataOptions} value={tempState.withdrawalMethod || undefined} onChange={value => setTempState(prev_1 => ({
          ...prev_1,
          withdrawalMethod: value
        }))} withoutCloseIcon />
            {tempState.withdrawalMethod === AffiliateWithdrawalTypesEnum.paypal && <FormInput label={t('global.email')} type={'email'} value={tempState.payPalCredentials?.email || ''} onChange={e_0 => setTempState(prev_2 => ({
          ...prev_2,
          payPalCredentials: {
            email: e_0.target.value
          }
        }))} error={errors.fields.payPalCredentials.email} required />}
            {tempState.withdrawalMethod === AffiliateWithdrawalTypesEnum.bank_transfer && <BankTransferTable affiliateSettingsData={userAffiliateSettings} mutateAffiliateSettings={mutateAffiliateSettings} />}
          </div>
          {errors.common && <>
              {errors.common.map((error, index) => <p className="text-red px-6 text-center text-sm" key={index}>
                  {error}
                </p>)}
            </>}
        </div> : <AffiliateSettingsSkeleton />}
    </SettingsLayout>;
};
export const getServerSideProps = getBaseServerSideProps(AffiliateSettingsLoco, 'global.settings');
export default AffiliateSettings;